import { defaultWidgetDimensionConfig } from '../components/widgets/consts';
import {
    FilterSelection,
    IColumnChart,
    ICreateWidget,
    IDonutChart,
    ILineChart,
    IPolarChart,
    IWidget,
    IWidgetPreview,
    IWidgetType,
} from '../services/api/widget/types';
import { channelNameTranslations } from '../services/api/interaction/consts';
import { __untypedExpression } from '../stores/expressions';

export function isGraphWidget(widget: IWidget<IWidgetType>): widget is IWidget<'Graph'> {
    return widget.widgetType === 'Graph';
}

export function isStaticGraphWidget(
    widget: IWidget<IWidgetType>
): widget is IWidget<'StaticGraph'> {
    return widget.widgetType === 'StaticGraph';
}

export function isTextWidget(widget: IWidget<IWidgetType>): widget is IWidget<'Text'> {
    return widget.widgetType === 'Text';
}

export function isTopListWidget(widget: IWidget<IWidgetType>): widget is IWidget<'TopList'> {
    return widget.widgetType === 'TopList';
}

export function isPromptWidget(widget: IWidget<IWidgetType>): widget is IWidget<'Prompt'> {
    return widget.widgetType === 'Prompt';
}

export function isGraphFormWidget(
    widget: ICreateWidget<IWidgetType>
): widget is ICreateWidget<'Graph'> {
    return widget.type === 'Graph';
}

export function isTextFormWidget(
    widget: ICreateWidget<IWidgetType>
): widget is ICreateWidget<'Text'> {
    return widget.type === 'Text';
}

export function isWidget(
    widget: IWidget<IWidgetType> | ICreateWidget<IWidgetType> | IWidgetPreview
): widget is IWidget<IWidgetType> {
    return Object.hasOwn(widget, 'id');
}

/**
 * A graph widget whose data has not been resolved yet (slim dashboard fetch). The per-widget
 * fetch fills in `graphWidget`; until then the widget should render a loading state.
 */
export function isGraphWidgetDataLoading(widget: IWidget<IWidgetType>): boolean {
    return isGraphWidget(widget) && !widget.graphWidget && !!widget.graphWidgetWithoutData;
}

export function isDonutChart(widget: IWidget<'Graph'>): widget is IWidget<'Graph', 'Donut'> {
    return isGraphWidget(widget) && widget.graphWidget?.graphType === 'Donut';
}

export function isLineChart(widget: IWidget<'Graph'>): widget is IWidget<'Graph', 'Line'> {
    return isGraphWidget(widget) && widget.graphWidget?.graphType === 'Line';
}

export function isColumnChart(widget: IWidget<'Graph'>): widget is IWidget<'Graph', 'Column'> {
    return isGraphWidget(widget) && widget.graphWidget?.graphType === 'Column';
}

export function isPieChart(widget: IWidget<'Graph'>): widget is IWidget<'Graph', 'Pie'> {
    return isGraphWidget(widget) && widget.graphWidget?.graphType === 'Pie';
}

export function isPolarChart(widget: IWidget<'Graph'>): widget is IWidget<'Graph', 'Polar'> {
    return isGraphWidget(widget) && widget.graphWidget?.graphType === 'Polar';
}

export function isPiePreviewChart(preview: IWidgetPreview): boolean {
    return preview.pieChart !== null;
}

export function isDonutPreviewChart(
    preview: IDonutChart | ILineChart | IColumnChart
): preview is IDonutChart {
    return typeof (preview as IDonutChart).donutChart?.value === 'number';
}

export function isLinePreviewChart(
    preview: IDonutChart | ILineChart | IColumnChart
): preview is ILineChart {
    return Array.isArray((preview as ILineChart).lineChart?.data);
}

export function isColumnPreviewChart(
    preview: IDonutChart | ILineChart | IColumnChart
): preview is IColumnChart {
    return Array.isArray((preview as IColumnChart).columnChart?.data);
}

export function isPolarPreviewChart(
    preview: IWidgetPreview
): preview is IWidgetPreview & IPolarChart {
    return Array.isArray((preview as IPolarChart).polarChart?.series);
}

export function createFilterSelectionString(filterSelection: FilterSelection): string {
    const fromTo =
        filterSelection.startTime.split('T')[0].replaceAll('-', '') +
        '-' +
        filterSelection.endTime.split('T')[0].replaceAll('-', '');

    const channels =
        filterSelection.channelFilter.length > 0
            ? filterSelection.channelFilter
                  .map((name) =>
                      channelNameTranslations[name as keyof typeof channelNameTranslations]
                          ? __untypedExpression(
                                channelNameTranslations[
                                    name as keyof typeof channelNameTranslations
                                ]
                            )
                          : name
                  )
                  .join(', ')
            : null;

    const categories =
        filterSelection.categoryFilter.length > 0
            ? filterSelection.categoryFilter.join(', ')
            : null;

    const levels =
        filterSelection.levelFilter.length > 0 ? filterSelection.levelFilter.join(', ') : null;

    const queues =
        filterSelection.queueFilter.length > 0 ? filterSelection.queueFilter.join(', ') : null;

    const skills =
        filterSelection.skillFilter.length > 0 ? filterSelection.skillFilter.join(', ') : null;

    const trackerFilter = filterSelection.trackerFilter
        ? filterSelection.trackerFilter.join(', ')
        : null;

    // ?
    const onlyResolved = filterSelection.onlyResolved ? 'Only Resolved' : null;
    const excludedWords =
        filterSelection.excludedWords.length > 0 ? filterSelection.excludedWords.join(', ') : null;
    const includedWords =
        filterSelection.includedWords.words.length > 0
            ? filterSelection.includedWords.words.join(', ')
            : null;
    const recordingEnd = filterSelection.recordingEnd
        ? filterSelection.recordingEnd.toString()
        : null;
    const recordingStart = filterSelection.recordingStart
        ? filterSelection.recordingStart.toString()
        : null;
    const sentimentAgentDirectionFilter = filterSelection.sentimentAgentDirectionFilter
        ? filterSelection.sentimentAgentDirectionFilter.join(', ')
        : null;
    const sentimentCustomerDirectionFilter = filterSelection.sentimentCustomerDirectionFilter
        ? filterSelection.sentimentCustomerDirectionFilter.join(', ')
        : null;
    const sentimentAgentFilter = filterSelection.sentimentAgentFilter
        ? filterSelection.sentimentAgentFilter.join(', ')
        : null;
    const sentimentCustomerFilter = filterSelection.sentimentCustomerFilter
        ? filterSelection.sentimentCustomerFilter.join(', ')
        : null;

    const filters: (string | null)[] = [
        fromTo,
        categories,
        channels,
        excludedWords,
        includedWords,
        levels,
        onlyResolved,
        queues,
        recordingEnd,
        recordingStart,
        sentimentAgentDirectionFilter,
        sentimentAgentFilter,
        sentimentCustomerDirectionFilter,
        sentimentCustomerFilter,
        skills,
        trackerFilter,
    ];

    return filters.reduce((filterText: string, currentFilter: string | null, index: number) => {
        return filterText + (index == 0 || !currentFilter ? '' : ', ') + (currentFilter || '');
    }, '');
}

export function getDefaultWidgetDimensions(widget: IWidget | ICreateWidget): {
    h: number;
    w: number;
} {
    let key: keyof typeof defaultWidgetDimensionConfig;
    if ('widgetType' in widget) {
        key =
            widget.widgetType === 'Graph' && isGraphWidget(widget)
                ? (widget.graphWidget?.graphType ??
                  widget.graphWidgetWithoutData?.graphType ??
                  widget.widgetType)
                : widget.widgetType;
    } else {
        key = isGraphFormWidget(widget) && widget.graphType ? widget.graphType : widget.type;
    }

    const { h, w } = defaultWidgetDimensionConfig[key];
    return { h, w };
}
